import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useToast } from "../admincomponents/AdminToast";
import customPagesApi from "../admincomponents/CustomPagesApi";
import { BASE_URL } from "../baseurl";
import { resolveAssetUrl } from "../baseurl";
const SERVER_ORIGIN = BASE_URL.replace(/\/api\/?$/, "");

function resolveImageUrl(value) {
  if (!value) return "";
  if (value.startsWith("data:") || value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${SERVER_ORIGIN}${value}`;
}

function slugify(text) {
  return text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}

function newBlockId() {
  return `blk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const WIDTHS = [
  { value: "contained", label: "Contained", desc: "Narrow reading column" },
  { value: "wide", label: "Wide", desc: "Wide layout" },
  { value: "full", label: "Full width", desc: "Edge to edge" },
];

const WIDTH_STYLE = {
  contained: { maxWidth: "52%" },
  wide: { maxWidth: "80%" },
  full: { maxWidth: "100%" },
};

const ALIGN_CLASS = {
  left: "items-start",
  center: "items-center",
  right: "items-end",
};

const CONTENT_ALIGN_CLASS = {
  left: "mr-auto",
  center: "mx-auto",
  right: "ml-auto",
};

function defaultSettings() {
  return {
    width: "contained",
    align: "left",
    contentAlign: "left",
    background: "transparent",
    textColor: "",
    paddingY: 16,
    paddingX: 16,
  };
}

function newColumn() {
  return { id: newBlockId(), type: "text", value: "<p>New column</p>", alt: "", imageWidth: 100 };
}

function makeBlock(type, order) {
  const base = { id: newBlockId(), type, order, settings: defaultSettings() };
  if (type === "heading") return { ...base, value: "New heading", level: "h2" };
  if (type === "text") return { ...base, value: "<p>Start writing...</p>" };
  if (type === "image") return { ...base, value: "", alt: "", imageWidth: 100, imageHeight: null };
  if (type === "spacer") return { ...base, height: 48 };
  if (type === "columns") return { ...base, columns: [newColumn(), newColumn()] };
  return base;
}

const BLOCKS = [
  ["heading", "Heading", "Section title", "T"],
  ["text", "Text", "Rich text paragraph", "≡"],
  ["image", "Image", "Single image", "▧"],
  ["columns", "Columns", "2–4 column layout", "▥"],
  ["spacer", "Spacer", "Vertical space", "↕"],
];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImagePicker({ value, alt, onValueChange, onAltChange, compact }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    try {
      setError("");
      onValueChange(await fileToDataUrl(file));
    } catch {
      setError("The image could not be read.");
    }
  };

  const inputClass = compact
    ? "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-800 focus:outline-none focus:border-indigo-400"
    : "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-indigo-400";

  return (
    <div className="flex flex-col gap-2">
      <input ref={inputRef} type="file" accept="image/*" onChange={chooseFile} className="hidden" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`w-full border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-colors font-medium ${compact ? "text-xs py-2" : "text-sm py-2.5"}`}
      >
        {value ? "Replace image" : "Choose image from your computer"}
      </button>
      {error && <p className="text-xs text-red-500">{error}</p>}
      <input value={alt || ""} onChange={(event) => onAltChange(event.target.value)} placeholder="Alt text" className={inputClass} />
    </div>
  );
}

function ImageBlock({ block, onChange, isPreview, compact = false }) {
  const patch = (value) => onChange({ ...block, ...value });
  return (
    <div className="w-full">
      {block.value ? (
        <img
        src={resolveAssetUrl(block.value)}
          alt={block.alt || ""}
          className="rounded-xl object-cover shadow-sm"
          style={{ width: `${block.imageWidth || 100}%`, height: block.imageHeight ? `${block.imageHeight}px` : "auto" }}
        />
      ) : (
        !isPreview && <div className="w-full aspect-video bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center text-sm text-gray-400">Choose an image below</div>
      )}
      {!isPreview && <div className="mt-3"><ImagePicker value={block.value} alt={block.alt} onValueChange={(value) => patch({ value })} onAltChange={(alt) => patch({ alt })} compact={compact} /></div>}
    </div>
  );
}

function BlockContent({ block, onChange, isPreview }) {
  const settings = block.settings || defaultSettings();
  const textStyle = settings.textColor ? { color: settings.textColor } : undefined;

  if (block.type === "heading") {
    const Tag = block.level || "h2";
    const size = block.level === "h1" ? "text-4xl" : block.level === "h3" ? "text-xl" : "text-2xl";
    return isPreview ? <Tag className={`${size} font-bold text-gray-900 w-full`} style={textStyle}>{block.value}</Tag> : (
      <input value={block.value || ""} onChange={(event) => onChange({ ...block, value: event.target.value })} placeholder="Heading text" className={`${size} font-bold text-gray-900 bg-transparent outline-none w-full placeholder:text-gray-300`} style={textStyle} />
    );
  }

  if (block.type === "text") {
    const plain = (block.value || "").replace(/<[^>]*>/g, "");
    return isPreview ? <p className="text-gray-600 leading-relaxed whitespace-pre-wrap w-full" style={textStyle}>{plain}</p> : (
      <textarea value={plain} onChange={(event) => onChange({ ...block, value: `<p>${event.target.value}</p>` })} placeholder="Start writing..." rows={4} className="w-full bg-transparent outline-none resize-none text-gray-600 leading-relaxed placeholder:text-gray-300" style={textStyle} />
    );
  }

  if (block.type === "image") return <ImageBlock block={block} onChange={onChange} isPreview={isPreview} />;

  if (block.type === "spacer") {
    return isPreview ? <div style={{ height: block.height || 48 }} /> : (
      <div style={{ height: Math.max(block.height || 48, 28) }} className="w-full rounded-xl bg-gray-50 flex items-center justify-center text-xs font-medium text-gray-300 border border-dashed border-gray-200">
        {block.height || 48}px spacer
      </div>
    );
  }

  if (block.type === "columns") {
    const columns = block.columns || [newColumn(), newColumn()];
    const updateColumn = (index, patch) => onChange({ ...block, columns: columns.map((column, i) => i === index ? { ...column, ...patch } : column) });
    return (
      <div className={`grid grid-cols-1 ${columns.length === 2 ? "sm:grid-cols-2" : columns.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4"} gap-6 w-full`}>
        {columns.map((column, index) => (
          <div key={column.id || index} className="flex flex-col gap-2">
            {column.type === "image" ? (
              <ImageBlock block={column} onChange={(updated) => updateColumn(index, updated)} isPreview={isPreview} compact />
            ) : isPreview ? (
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap" style={textStyle}>{(column.value || "").replace(/<[^>]*>/g, "")}</p>
            ) : (
              <textarea value={(column.value || "").replace(/<[^>]*>/g, "")} onChange={(event) => updateColumn(index, { value: `<p>${event.target.value}</p>` })} rows={3} placeholder={`Column ${index + 1}`} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 outline-none resize-none text-gray-600 leading-relaxed" />
            )}
            {!isPreview && (
              <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                <button type="button" onClick={() => updateColumn(index, { type: "text" })} className={`flex-1 text-xs py-1.5 rounded-md ${column.type !== "image" ? "bg-white shadow-sm text-gray-800" : "text-gray-500"}`}>Text</button>
                <button type="button" onClick={() => updateColumn(index, { type: "image" })} className={`flex-1 text-xs py-1.5 rounded-md ${column.type === "image" ? "bg-white shadow-sm text-gray-800" : "text-gray-500"}`}>Image</button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return null;
}

function SortableBlock({ block, selected, onSelect, onChange, onDelete, onDuplicate, isPreview }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id, disabled: isPreview });
  const settings = block.settings || defaultSettings();
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }} className={`relative w-full flex ${settings.align === "center" ? "justify-center" : settings.align === "right" ? "justify-end" : "justify-start"}`}>
      <div
        onClick={(event) => { if (!isPreview) { event.stopPropagation(); onSelect(block.id); } }}
        className={`w-full flex flex-col ${ALIGN_CLASS[settings.align] || ALIGN_CLASS.left} rounded-xl ${!isPreview && selected ? "ring-2 ring-indigo-500 ring-offset-4" : !isPreview ? "ring-1 ring-transparent hover:ring-indigo-200 cursor-pointer" : ""}`}
        style={{ ...WIDTH_STYLE[settings.width] || WIDTH_STYLE.contained, background: settings.background === "transparent" ? "transparent" : settings.background, padding: `${settings.paddingY}px ${settings.paddingX}px` }}
      >
        {!isPreview && selected && <span className="absolute -top-3 left-3 z-10 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1">{block.type}</span>}
        <BlockContent block={block} onChange={onChange} isPreview={isPreview} />
      </div>
      {!isPreview && selected && (
        <div className="absolute -right-2 top-1/2 translate-x-full -translate-y-1/2 flex flex-col gap-1.5 pl-3">
          <button type="button" {...attributes} {...listeners} title="Drag to reorder" className="w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-500 shadow-sm cursor-grab">⠿</button>
          <button type="button" onClick={(event) => { event.stopPropagation(); onDuplicate(block.id); }} title="Duplicate" className="w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-500 shadow-sm">＋</button>
          <button type="button" onClick={(event) => { event.stopPropagation(); onDelete(block.id); }} title="Delete" className="w-8 h-8 rounded-lg bg-white border border-gray-200 text-red-400 shadow-sm">×</button>
        </div>
      )}
    </div>
  );
}

function SegmentedControl({ value, options, onChange }) {
  return <div className="flex gap-1">{options.map((option) => <button type="button" key={option} onClick={() => onChange(option)} className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border capitalize ${value === option ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-500 border-gray-200"}`}>{option}</button>)}</div>;
}

function SettingsSidebar({ block, page, onBlockChange, onPageChange }) {
  if (!block) {
    return (
      <div className="flex flex-col gap-5 p-5">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Page settings</p>
        <div><label className="block text-xs font-semibold text-gray-600 mb-2">Status</label><SegmentedControl value={page.status} options={["Draft", "Published"]} onChange={(status) => onPageChange({ status })} /></div>
        <div><label className="block text-xs font-semibold text-gray-600 mb-2">URL slug</label><input value={page.slug} onChange={(event) => onPageChange({ slug: slugify(event.target.value), slugEdited: true })} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-semibold text-gray-600 mb-2">Page background</label><input type="color" value={page.background || "#ffffff"} onChange={(event) => onPageChange({ background: event.target.value })} className="w-10 h-9 rounded border border-gray-200" /></div>
        <p className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-xl p-3">Select a block on the canvas to edit its layout, spacing, and appearance.</p>
      </div>
    );
  }

  const settings = block.settings || defaultSettings();
  const update = (patch) => onBlockChange({ ...block, settings: { ...settings, ...patch } });
  return (
    <div className="flex flex-col gap-5 p-5">
      <div><p className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest">{block.type}</p><p className="text-sm font-semibold text-gray-900">Block settings</p></div>
      {block.type === "heading" && <div><label className="block text-xs font-semibold text-gray-600 mb-2">Heading level</label><SegmentedControl value={block.level} options={["h1", "h2", "h3"]} onChange={(level) => onBlockChange({ ...block, level })} /></div>}
      {block.type === "columns" && <div><label className="block text-xs font-semibold text-gray-600 mb-2">Columns</label><SegmentedControl value={(block.columns || []).length} options={[2, 3, 4]} onChange={(count) => { const columns = [...(block.columns || [])]; while (columns.length < count) columns.push(newColumn()); onBlockChange({ ...block, columns: columns.slice(0, count) }); }} /></div>}
      {block.type === "image" && <div><label className="block text-xs font-semibold text-gray-600 mb-2">Width: {block.imageWidth || 100}%</label><input type="range" min="5" max="100" value={block.imageWidth || 100} onChange={(event) => onBlockChange({ ...block, imageWidth: Number(event.target.value) })} className="w-full accent-indigo-600" /></div>}
      {block.type === "spacer" && <div><label className="block text-xs font-semibold text-gray-600 mb-2">Height: {block.height || 48}px</label><input type="range" min="8" max="200" value={block.height || 48} onChange={(event) => onBlockChange({ ...block, height: Number(event.target.value) })} className="w-full accent-indigo-600" /></div>}
      <div><label className="block text-xs font-semibold text-gray-600 mb-2">Width</label><div className="flex flex-col gap-1.5">{WIDTHS.map((width) => <button type="button" key={width.value} onClick={() => update({ width: width.value })} className={`text-left px-3 py-2 rounded-xl border text-sm ${settings.width === width.value ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-gray-200 text-gray-600"}`}><span className="block text-xs font-semibold">{width.label}</span><span className="block text-[11px] text-gray-400">{width.desc}</span></button>)}</div></div>
      <div><label className="block text-xs font-semibold text-gray-600 mb-2">Container position</label><SegmentedControl value={settings.align} options={["left", "center", "right"]} onChange={(align) => update({ align })} /></div>
      <div><label className="block text-xs font-semibold text-gray-600 mb-2">Content alignment</label><SegmentedControl value={settings.contentAlign} options={["left", "center", "right"]} onChange={(contentAlign) => update({ contentAlign })} /></div>
      <div><label className="block text-xs font-semibold text-gray-600 mb-2">Vertical padding: {settings.paddingY}px</label><input type="range" min="0" max="96" value={settings.paddingY} onChange={(event) => update({ paddingY: Number(event.target.value) })} className="w-full accent-indigo-600" /></div>
      <div><label className="block text-xs font-semibold text-gray-600 mb-2">Horizontal padding: {settings.paddingX}px</label><input type="range" min="0" max="96" value={settings.paddingX} onChange={(event) => update({ paddingX: Number(event.target.value) })} className="w-full accent-indigo-600" /></div>
    </div>
  );
}

export default function LegacyCustomPageEditor() {
  const { id: pageId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState({ title: "", slug: "", status: "Draft", background: "#ffffff", content: [] });
  const [loading, setLoading] = useState(Boolean(pageId));
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(false);
  const [insertOpen, setInsertOpen] = useState(false);
  const insertRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (!pageId) return;
    customPagesApi.list()
      .then((pages) => pages.find((item) => item._id === pageId))
      .then((item) => {
        if (!item) throw new Error("Page not found");
        return customPagesApi.get(item.slug);
      })
      .then((loaded) => setPage({ ...loaded, content: (loaded.content || []).sort((a, b) => a.order - b.order).map((block) => ({ ...block, id: newBlockId(), settings: block.settings || defaultSettings() })) }))
      .catch((error) => toast({ variant: "error", title: "Couldn't load page", description: error.message }))
      .finally(() => setLoading(false));
  }, [pageId, toast]);

  useEffect(() => {
    const close = (event) => { if (insertRef.current && !insertRef.current.contains(event.target)) setInsertOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const blocks = page.content || [];
  const updatePage = (patch) => setPage((current) => ({ ...current, ...patch }));
  const updateBlock = (block) => setPage((current) => ({ ...current, content: current.content.map((item) => item.id === block.id ? block : item) }));
  const addBlock = (type) => { const block = makeBlock(type, blocks.length); setPage((current) => ({ ...current, content: [...current.content, block] })); setSelectedId(block.id); setInsertOpen(false); };
  const deleteBlock = (id) => { setPage((current) => ({ ...current, content: current.content.filter((block) => block.id !== id) })); if (selectedId === id) setSelectedId(null); };
  const duplicateBlock = (id) => setPage((current) => { const index = current.content.findIndex((block) => block.id === id); if (index < 0) return current; const clone = { ...current.content[index], id: newBlockId() }; const content = [...current.content]; content.splice(index + 1, 0, clone); return { ...current, content: content.map((block, i) => ({ ...block, order: i })) }; });
  const dragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setPage((current) => {
      const oldIndex = current.content.findIndex((block) => block.id === active.id);
      const newIndex = current.content.findIndex((block) => block.id === over.id);
      return { ...current, content: arrayMove(current.content, oldIndex, newIndex).map((block, i) => ({ ...block, order: i })) };
    });
  };

  const save = async () => {
    if (!page.title.trim() || !page.slug.trim()) return toast({ variant: "error", title: "Missing information", description: "Title and slug are required." });
    if (!blocks.length) return toast({ variant: "error", title: "Empty page", description: "Add at least one block." });
    setSaving(true);
    try {
      const payload = { title: page.title, slug: page.slug, status: page.status, background: page.background, blocks: blocks.map((block, i) => ({ ...block, order: i })) };
      if (pageId) await customPagesApi.update(pageId, payload);
      else await customPagesApi.create(payload);
      toast({ variant: "success", title: pageId ? "Page updated" : "Page created" });
      navigate("/admin/custom-pages");
    } catch (error) {
      toast({ variant: "error", title: "Save failed", description: error.response?.data?.message || error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Loading page...</div>;

  const selectedBlock = blocks.find((block) => block.id === selectedId) || null;
  return (
    <div className="w-full h-full flex flex-col bg-[#F5F5F0]">
      <header className="flex items-center justify-between bg-white border-b border-gray-200 px-4 h-14 shrink-0 z-10">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" onClick={() => navigate("/admin/custom-pages")} className="w-8 h-8 rounded-lg text-gray-500 hover:bg-gray-100">←</button>
          <span className="text-xs text-gray-400 hidden sm:block">Custom Pages /</span>
          <input value={page.title} onChange={(event) => updatePage({ title: event.target.value, ...(!page.slug ? { slug: slugify(event.target.value) } : {}) })} placeholder="Untitled page" className="text-sm font-semibold text-gray-900 bg-transparent outline-none w-48 px-2 py-1" />
          <span className={`hidden md:inline-flex text-[11px] font-semibold rounded-full px-2.5 py-1 border ${page.status === "Published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>{page.status}</span>
        </div>
        <div className="flex items-center bg-gray-100 rounded-xl p-1">
          <button type="button" onClick={() => setPreview(false)} className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold ${!preview ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>Edit</button>
          <button type="button" onClick={() => setPreview(true)} className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold ${preview ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>Preview</button>
        </div>
        <button type="button" onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold text-xs rounded-xl px-4 py-2.5">{saving ? "Saving..." : "Save page"}</button>
      </header>

      <div className="flex flex-1 min-h-0">
        {!preview && (
          <aside className="w-[196px] shrink-0 bg-[#1E1E2E] border-r border-white/5 flex flex-col overflow-y-auto">
            <div className="px-4 pt-5">
              <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest mb-3">Blocks</p>
              {BLOCKS.map(([type, label, hint, icon]) => <button type="button" key={type} onClick={() => addBlock(type)} className="group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/10 text-left w-full"><span className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center text-white/60">{icon}</span><span><span className="block text-xs font-medium text-white/70 group-hover:text-white">{label}</span><span className="block text-[10px] text-white/30">{hint}</span></span></button>)}
            </div>
            <div className="mt-auto border-t border-white/5 px-4 py-4 text-white/30 text-[11px]">{blocks.length} block{blocks.length === 1 ? "" : "s"}</div>
          </aside>
        )}

        <main className="flex-1 overflow-y-auto" style={{ background: "radial-gradient(circle at 1px 1px, #d1d5db 1px, transparent 0) repeat, #F5F5F0", backgroundSize: "24px 24px" }} onClick={() => setSelectedId(null)}>
          <div className="max-w-5xl mx-auto py-10 px-6">
            {!preview && <div className="flex items-center justify-between bg-gray-50 border border-b-0 border-gray-200 rounded-t-2xl px-4 py-2.5"><span className="text-xs text-gray-400">/{page.slug || "untitled"}</span><span className="text-xs text-gray-400">{blocks.length} blocks</span></div>}
            <div onClick={(event) => event.stopPropagation()} style={{ background: page.background || "#ffffff" }} className={`border border-gray-200 ${preview ? "rounded-2xl" : "rounded-b-2xl"} shadow-xl min-h-[70vh] py-14 px-8 flex flex-col gap-6`}>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dragEnd}>
                <SortableContext items={blocks.map((block) => block.id)} strategy={verticalListSortingStrategy}>
                  {blocks.map((block) => <SortableBlock key={block.id} block={block} selected={selectedId === block.id} onSelect={setSelectedId} onChange={updateBlock} onDelete={deleteBlock} onDuplicate={duplicateBlock} isPreview={preview} />)}
                </SortableContext>
              </DndContext>
              {!blocks.length && !preview && <div className="flex flex-col items-center justify-center py-28 text-center text-gray-400"><p className="font-semibold text-sm text-gray-700">Canvas is empty</p><p className="text-xs mt-1">Choose a block from the sidebar to begin.</p></div>}
              {!preview && <div className="relative flex justify-center pt-4 border-t border-dashed border-gray-200" ref={insertRef}><button type="button" onClick={() => setInsertOpen((value) => !value)} className="text-xs font-semibold text-gray-400 hover:text-indigo-600 border border-dashed border-gray-200 rounded-xl px-5 py-2.5">＋ Add block</button>{insertOpen && <div className="absolute z-20 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-2xl p-2">{BLOCKS.map(([type, label, hint]) => <button type="button" key={type} onClick={() => addBlock(type)} className="flex flex-col w-full text-left px-3 py-2 rounded-xl hover:bg-indigo-50"><span className="text-xs font-semibold text-gray-800">{label}</span><span className="text-[11px] text-gray-400">{hint}</span></button>)}</div>}</div>}
            </div>
          </div>
        </main>

        {!preview && <aside className="w-72 shrink-0 border-l border-gray-200 bg-white overflow-y-auto"><SettingsSidebar block={selectedBlock} page={page} onBlockChange={updateBlock} onPageChange={updatePage} /></aside>}
      </div>
    </div>
  );
}